import type { PrimitiveAtom } from 'jotai';
import { atom } from 'jotai';
import { atomFamily, atomWithReset } from 'jotai/utils';

import type { Action, Progress } from 'src/types/planAction';

export const touchedAtom = atom<boolean>(false);

export const actionKPIAtom = atomFamily(() =>
  atomWithReset<number | undefined>(undefined),
);

export const actionTargetStatementAtom = atomFamily(() =>
  atom<number | null>(null),
);

export const actionKPISelector = atomFamily((actionId: number) =>
  atom(
    (get) => get(actionKPIAtom(actionId)),
    (get, set, newValue: number) => {
      if (newValue !== get(actionKPIAtom(actionId))) {
        set(actionTargetStatementAtom(actionId), null);
      }
      set(actionKPIAtom(actionId), newValue);
    },
  ),
);

export const actionTitleAtom = atomFamily(() => atom(''));

export const actionNoteAtom = atomFamily(() => atom(''));

export const actionPinnedFilesAtom = atomFamily<
  number | undefined,
  PrimitiveAtom<any>
>(() => atom([]));

export const actionSharedAtom = atomFamily(() => atom(false));

export const actionOwnerAtom = atomFamily(() => atom(''));

export const actionDeadlineAtom = atomFamily<
  number | undefined,
  PrimitiveAtom<string | undefined>
>(() => atom<string | undefined>(undefined));

export const actionProgressAtom = atomFamily<
  number | undefined,
  PrimitiveAtom<Progress | undefined>
>(() => atom<Progress | undefined>(undefined));

export const actionIndexAtom = atom<number>(-1);

export const actionsAtom = atom<number[]>([]);

export const createNewActionSelector = atom(null, (get, set) => {
  const index = get(actionIndexAtom);
  set(actionIndexAtom, (prev) => prev - 1);
  set(actionsAtom, (prev) => [...prev, index]);
});

export const apiActionSelector = atom(
  (get): Action[] =>
    get(actionsAtom).map((actionId) => ({
      id: actionId > 0 ? actionId : undefined,
      kpi: get(actionKPIAtom(actionId)),
      target_statement: get(actionTargetStatementAtom(actionId)),
      title: get(actionTitleAtom(actionId)),
      notes: get(actionNoteAtom(actionId)),
      attachment_ids: get(actionPinnedFilesAtom(actionId)).map(
        ({ id }: { id: number }) => id,
      ),
      attachments: get(actionPinnedFilesAtom(actionId)),
      shared: get(actionSharedAtom(actionId)),
      owner: get(actionOwnerAtom(actionId)),
      deadline: get(actionDeadlineAtom(actionId)),
      progress: get(actionProgressAtom(actionId)),
      plan: undefined,
    })),
  (_, set, actionsArray: Action[]) => {
    actionsArray.forEach((action) => {
      set(actionKPIAtom(action.id), action.kpi);
      set(actionTargetStatementAtom(action.id), action.target_statement);
      set(actionTitleAtom(action.id), action.title);
      set(actionNoteAtom(action.id), action.notes);
      set(
        actionPinnedFilesAtom(action.id),
        action.attachments.map((file) => ({
          ...file,
          key: file.file,
        })),
      );
      set(actionSharedAtom(action.id), action.shared);
      set(actionOwnerAtom(action.id), action.owner);
      set(actionDeadlineAtom(action.id), action.deadline);
      set(actionProgressAtom(action.id), action.progress);
    });
    set(
      actionsAtom,
      actionsArray.reduce(
        (acc: number[], { id }) => (id ? [...acc, id] : acc),
        [],
      ),
    );
  },
);
